import { type ProductAttributes } from './types';

import { Language } from '@/modules/language/constants';
import {
  type AttributeTranslations,
  type GetAttributesResponse,
} from '@/modules/productAttributes/aliases';
import { type StyleAttribute } from '@/modules/search/types';

type TranslatableGroup = GetAttributesResponse['style'];

function translate(
  translations: AttributeTranslations | undefined,
  language: string
) {
  return (
    translations?.[language as keyof AttributeTranslations] ??
    translations?.[Language.en] ??
    ''
  );
}

function toStyleAttribute(
  group: TranslatableGroup,
  language: string
): StyleAttribute {
  return {
    nameI18N: translate(group.name_i18n, language),
    descriptionI18N: translate(group.description_i18n, language),
    status: group.status,
    maxSelection: group.max_selection,
    options: group.attributes,
  };
}

export function toProductAttributes(
  data: GetAttributesResponse,
  language: string
): ProductAttributes {
  return {
    age: toStyleAttribute(data.age, language),
    brand: data.brand,
    colour: data.colour.attributes.map(
      ({ id, status, name_i18n, hex_code }) => ({
        id,
        status,
        nameI18N: translate(name_i18n, language),
        hexCode: hex_code,
      })
    ),
    condition: data.condition.attributes.map(
      ({ id, status, name_i18n, description_i18n }) => ({
        id,
        status,
        nameI18N: translate(name_i18n, language),
        descriptionI18N: translate(description_i18n, language),
      })
    ),
    department: data.department,
    gender: data.gender,
    genericAttributes: data.generic_attributes,
    group: data.group,
    source: toStyleAttribute(data.source, language),
    style: toStyleAttribute(data.style, language),
  };
}
